import Link from '@mui/material/Link';
import { forwardRef } from 'react';
import { Link as ReactRouterLink } from 'react-router-dom';
import { getEyeAbleUrl } from '@helpers/functions';
import { useInlineTranslation } from '@/i18n/hooks/useInlineTranslation.tsx';

/**
 * @typedef {import('@mui/material/Link').LinkProps & {
 *   to?: string;
 *   external?: boolean;
 * }} RouterLinkProps
 */

/** @type {React.ForwardRefExoticComponent<RouterLinkProps & React.RefAttributes<any>>} */
const RouterLink = forwardRef(
  function RouterLinkRef(/** @type {RouterLinkProps} */ props, ref) {
    const { i18n } = useInlineTranslation();
    const lng = i18n.resolvedLanguage;

    const { to, href, external, sx, ...rest } = props;

    let link = href || to;
    const eyeAbleUrl = getEyeAbleUrl(link, lng);

    let isExternal = external || !!link?.startsWith?.('http');

    if (eyeAbleUrl) {
      isExternal = true;

      link = eyeAbleUrl;
    } else if (isExternal) {
      if (link?.startsWith?.('https://eye-able.com/')) {
        let url = new URL(link);
        let path = url.pathname; // eg '/wcag-checker/'
        let origin = url.origin; // 'https://services.eye-able.com'

        // fix for audit rules where each link starts with en
        if (path.includes('/en/eye-able-audit-regel-')) {
          origin = 'https://services.eye-able.com';
          path = path.replace('/en/', '/' + lng + '/');
        } else if (path.startsWith('/en/')) {
          if (lng === 'en')
            path = path.replace('/en/', '/'); // english site must not have language prefix
          else path = path.replace('/en/', '/' + lng + '/');
        } else if (path.startsWith('/de/')) {
          path = path.replace('/de/', '/' + lng + '/');
        } else if (lng !== 'en') {
          // english site must not have language prefix
          path = '/' + i18n.resolvedLanguage + path;
        }

        link = origin + path;
      } else if (link?.startsWith?.('https://services.eye-able.com/')) {
        let url = new URL(link);
        let path = url.pathname; // eg '/wcag-checker/'
        let origin = url.origin; // 'https://services.eye-able.com'

        if (path.startsWith('/en/')) {
          path = path.replace('/en/', '/' + lng + '/');
        } else if (path.startsWith('/de/')) {
          path = path.replace('/de/', '/' + lng + '/');
        } else {
          path = '/' + i18n.resolvedLanguage + path;
        }

        link = origin + path;
      }
    }

    const linkProps = {
      fontSize: 'inherit',
      underline: 'hover',
      role: undefined,
      target: isExternal ? '_blank' : '_self',
      rel: 'noopener noreferrer',
      color: isExternal ? '#003fab' : 'inherit',
      sx: (theme) => ({
        textDecorationColor: 'inherit !important',
        '&:focus:not(.MuiButtonBase-root)': {
          outline: `3px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px',
        },
        '&:hover:not(.MuiButtonBase-root)': isExternal
          ? {
              filter: 'brightness(1.25)',
            }
          : {},
        ...sx,
      }),
      ...rest,
      to: link,
      href: link,
    };

    return (
      <Link {...linkProps} component={ReactRouterLink} ref={ref}>
        {linkProps.children}
      </Link>
    );
  },
);

export default RouterLink;
